import { createApi } from '@reduxjs/toolkit/query/react';
import { overrideFetchBaseQuery } from '../lib/utils/overrideFetchBaseQuery';
import type {
  CreateMonthlyBrandManagerTargetPayload,
  CreateMonthlyStoreTargetPayload,
  CreateMonthlyStylistTargetPayload,
  CreateWeeklyStoreTargetPayload,
  MonthlyBrandManagerTarget,
  MonthlyStoreTarget,
  MonthlyStylistTarget,
  UpdateMonthlyBrandManagerTargetPayload,
  UpdateMonthlyStoreTargetPayload,
  UpdateMonthlyStylistTargetPayload,
  UpdateWeeklyStoreTargetPayload,
  WeeklyStoreTarget,
} from '@admin-panel/providers';

export const goalpostApi = createApi({
  reducerPath: 'goalpostApi',
  baseQuery: overrideFetchBaseQuery({ urlSuffix: 'goalpost' }),
  tagTypes: [
    'MonthlyStoreTarget',
    'WeeklyStoreTarget',
    'MonthlyStylistTarget',
    'MonthlyBrandManagerTarget',
  ],
  endpoints: builder => ({
    // ── Monthly Store Targets ────────────────────────────────────────────────
    getMonthlyStoreTargets: builder.query<MonthlyStoreTarget[], void>({
      providesTags: ['MonthlyStoreTarget'],
      query: () => '/monthly-store-targets',
    }),
    createMonthlyStoreTarget: builder.mutation<MonthlyStoreTarget, CreateMonthlyStoreTargetPayload>(
      {
        invalidatesTags: ['MonthlyStoreTarget'],
        query: body => ({ body, method: 'POST', url: '/monthly-store-targets' }),
      },
    ),
    updateMonthlyStoreTarget: builder.mutation<
      MonthlyStoreTarget,
      { id: string } & UpdateMonthlyStoreTargetPayload
    >({
      invalidatesTags: ['MonthlyStoreTarget'],
      query: ({ id, ...body }) => ({ body, method: 'PATCH', url: `/monthly-store-targets/${id}` }),
    }),

    // ── Weekly Store Targets (IB & Non-Apparel) ──────────────────────────────
    getWeeklyStoreTargets: builder.query<WeeklyStoreTarget[], void>({
      providesTags: ['WeeklyStoreTarget'],
      query: () => '/weekly-store-targets',
    }),
    createWeeklyStoreTarget: builder.mutation<WeeklyStoreTarget, CreateWeeklyStoreTargetPayload>({
      invalidatesTags: ['WeeklyStoreTarget'],
      query: body => ({ body, method: 'POST', url: '/weekly-store-targets' }),
    }),
    updateWeeklyStoreTarget: builder.mutation<
      WeeklyStoreTarget,
      { id: string } & UpdateWeeklyStoreTargetPayload
    >({
      invalidatesTags: ['WeeklyStoreTarget'],
      query: ({ id, ...body }) => ({ body, method: 'PATCH', url: `/weekly-store-targets/${id}` }),
    }),

    // ── Monthly Store Targets (delete) ──────────────────────────────────────
    deleteMonthlyStoreTarget: builder.mutation<void, string>({
      invalidatesTags: ['MonthlyStoreTarget'],
      query: id => ({ method: 'DELETE', url: `/monthly-store-targets/${id}` }),
    }),

    // ── Weekly Store Targets (delete) ────────────────────────────────────────
    deleteWeeklyStoreTarget: builder.mutation<void, string>({
      invalidatesTags: ['WeeklyStoreTarget'],
      query: id => ({ method: 'DELETE', url: `/weekly-store-targets/${id}` }),
    }),

    // ── Monthly Stylist Targets ──────────────────────────────────────────────
    getMonthlyStylistTargets: builder.query<MonthlyStylistTarget[], void>({
      providesTags: ['MonthlyStylistTarget'],
      query: () => '/monthly-stylist-targets',
    }),
    createMonthlyStylistTarget: builder.mutation<
      MonthlyStylistTarget,
      CreateMonthlyStylistTargetPayload
    >({
      invalidatesTags: ['MonthlyStylistTarget'],
      query: body => ({ body, method: 'POST', url: '/monthly-stylist-targets' }),
    }),
    updateMonthlyStylistTarget: builder.mutation<
      MonthlyStylistTarget,
      { id: string } & UpdateMonthlyStylistTargetPayload
    >({
      invalidatesTags: ['MonthlyStylistTarget'],
      query: ({ id, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/monthly-stylist-targets/${id}`,
      }),
    }),
    deleteMonthlyStylistTarget: builder.mutation<void, string>({
      invalidatesTags: ['MonthlyStylistTarget'],
      query: id => ({ method: 'DELETE', url: `/monthly-stylist-targets/${id}` }),
    }),

    // ── Monthly Brand Manager Targets ──────────────────────────────────────────
    getMonthlyBrandManagerTargets: builder.query<MonthlyBrandManagerTarget[], void>({
      providesTags: ['MonthlyBrandManagerTarget'],
      query: () => '/monthly-brand-manager-targets',
    }),
    createMonthlyBrandManagerTarget: builder.mutation<
      MonthlyBrandManagerTarget,
      CreateMonthlyBrandManagerTargetPayload
    >({
      invalidatesTags: ['MonthlyBrandManagerTarget'],
      query: body => ({ body, method: 'POST', url: '/monthly-brand-manager-targets' }),
    }),
    updateMonthlyBrandManagerTarget: builder.mutation<
      MonthlyBrandManagerTarget,
      { id: string } & UpdateMonthlyBrandManagerTargetPayload
    >({
      invalidatesTags: ['MonthlyBrandManagerTarget'],
      query: ({ id, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/monthly-brand-manager-targets/${id}`,
      }),
    }),
    deleteMonthlyBrandManagerTarget: builder.mutation<void, string>({
      invalidatesTags: ['MonthlyBrandManagerTarget'],
      query: id => ({ method: 'DELETE', url: `/monthly-brand-manager-targets/${id}` }),
    }),
  }),
});

export const {
  useGetMonthlyStoreTargetsQuery,
  useCreateMonthlyStoreTargetMutation,
  useUpdateMonthlyStoreTargetMutation,
  useDeleteMonthlyStoreTargetMutation,
  useGetWeeklyStoreTargetsQuery,
  useCreateWeeklyStoreTargetMutation,
  useUpdateWeeklyStoreTargetMutation,
  useDeleteWeeklyStoreTargetMutation,
  useGetMonthlyStylistTargetsQuery,
  useCreateMonthlyStylistTargetMutation,
  useUpdateMonthlyStylistTargetMutation,
  useDeleteMonthlyStylistTargetMutation,
  useGetMonthlyBrandManagerTargetsQuery,
  useCreateMonthlyBrandManagerTargetMutation,
  useUpdateMonthlyBrandManagerTargetMutation,
  useDeleteMonthlyBrandManagerTargetMutation,
} = goalpostApi;
